import { CRAFTED_NOT_IN_PLAN_MESSAGE, type SourceUpdateHeld, type TaskSourceUpdate } from '@/api'

type HeldChanges = Pick<
    TaskSourceUpdate,
    'addedTools' | 'addedGates' | 'removedTools' | 'removedGates' | 'missingInputs' | 'workspaceChanged'
>

/** `a`, `a and b`, `a, b and c`. */
function listed(items: string[]): string {
    return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}` : (items[0] ?? '')
}

/** `adds the tool X` / `adds the tools X and Y`; nothing for no tools. */
function toolsClause(verb: string, tools: string[], toolLabel: (toolName: string) => string): string[] {
    if (tools.length === 0) return []
    return [`${verb} the ${tools.length === 1 ? 'tool' : 'tools'} ${listed(tools.map(toolLabel))}`]
}

/** `adds an approval gate` / `removes 2 approval gates`; nothing for no gates. */
function gatesClause(verb: string, count: number): string[] {
    if (count === 0) return []
    return [count === 1 ? `${verb} an approval gate` : `${verb} ${count} approval gates`]
}

/** What the update changes that a person has to approve, in the order the panel lists it. */
function changeClauses(update: HeldChanges, toolLabel: (toolName: string) => string): string[] {
    const inputs = (update.missingInputs ?? []).map(input => input.label)
    return [
        ...toolsClause('adds', update.addedTools, toolLabel),
        ...gatesClause('adds', update.addedGates.length),
        ...toolsClause('removes', update.removedTools ?? [], toolLabel),
        ...gatesClause('removes', (update.removedGates ?? []).length),
        ...(inputs.length === 0 ? [] : [`needs ${inputs.length === 1 ? 'a value' : 'values'} for ${listed(inputs)}`]),
        ...(update.workspaceChanged ? ['changes its workspace credentials or write and push rules'] : []),
    ]
}

/**
 * The one "Not applied automatically: …" line for a held source update (#763), naming every reason the server gave,
 * in its order: the plan's own sentence first, as the Update refusal gives it, then auto-update being off, then what
 * the update changes that a person has to approve, built from the lists the panel already shows. Tools read through
 * `toolLabel`, the label the panel's tool chips use. Empty when there is nothing to name.
 */
export function heldSourceUpdateLine(
    update: HeldChanges,
    held: SourceUpdateHeld,
    toolLabel: (toolName: string) => string = name => name,
): string {
    const plan = held.reasons.includes('crafted_not_in_plan') ? (held.message ?? CRAFTED_NOT_IN_PLAN_MESSAGE) : ''
    const changes = held.reasons.includes('needs_review') ? changeClauses(update, toolLabel) : []
    const rest = [
        ...(held.reasons.includes('auto_update_off') ? ['auto-update is off'] : []),
        ...(changes.length > 0 ? [`this update ${listed(changes)}`] : []),
    ].join(', and ')
    if (plan && rest) return `Not applied automatically: ${plan} Also, ${rest}.`
    if (plan) return `Not applied automatically: ${plan}`
    return rest ? `Not applied automatically: ${rest}.` : ''
}
